import { useEffect, useRef, useState, ReactNode } from 'react';

export type PopoverAlign = 'start' | 'end';
export type PopoverSide = 'top' | 'bottom';

export interface PopoverProps {
  trigger: ReactNode;
  content: ReactNode;
  align?: PopoverAlign;
  side?: PopoverSide;
  /** Controlled mode - jeśli podany, popover jest kontrolowany z zewnątrz. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * Popover z dowolną zawartością. Jak Dropdown ale content nie jest listą itemów
 * — może zawierać formularze, color picker, preview, itd. Zamyka się na click
 * outside lub Escape. Controlled opcjonalnie.
 */
export function Popover({
  trigger,
  content,
  align = 'start',
  side = 'bottom',
  open: controlledOpen,
  onOpenChange,
  className,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const position = [
    side === 'bottom' ? 'top-full mt-1.5' : 'bottom-full mb-1.5',
    align === 'end' ? 'right-0' : 'left-0',
  ].join(' ');

  return (
    <div ref={containerRef} className={['relative inline-block', className].filter(Boolean).join(' ')}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div
          role="dialog"
          className={[
            'absolute z-50 min-w-[12rem] rounded-xl bg-surface-raised border border-border shadow-glass backdrop-blur-xl animate-scale-in',
            side === 'bottom' ? 'origin-top' : 'origin-bottom',
            position,
          ].join(' ')}
        >
          {content}
        </div>
      )}
    </div>
  );
}
